import React, { useEffect, useRef } from 'react';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import SurfaceTract from 'src/models/SurfaceTract';
import { TweenLite } from 'gsap';
import Card from 'src/components/common/Card.redesign';
import Ownership from 'src/models/tract_detail/Ownership';
import MathUtil from 'src/util/MathUtil';
import AgreementUtil from 'src/util/AgreementUtil';

interface ISurfaceTractListCardProps {
    key?: string;
    actions?: ContextMenuAction[];
    surfaceTract: SurfaceTract;
    onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
}

const SurfaceTractListCard = (props: ISurfaceTractListCardProps) => {
    const legDescEl = useRef(null);

    useEffect(() => {
        TweenLite.to(legDescEl.current, 0, { height: '2.5rem', background: 'transparent', borderColor: 'transparent', zIndex: 0 });
    }, []);

    const toggleLegalDesc = (state: boolean) => {
        if (state) {
            TweenLite.to(legDescEl.current, 0.2, { height: 'auto', background: 'white', borderColor: '#ccc', zIndex: 10 });
        } else {
            TweenLite.to(legDescEl.current, 0.2, { height: '2.5rem', background: 'transparent', borderColor: 'transparent', zIndex: 0 });
        }
    };

    const badgeInfo = (
        <span style={{ fontSize: '.9rem', position: 'relative', marginTop: '.2rem', display: 'block' }}>
            <span style={{ paddingLeft: '1rem', fontWeight: 600 }}>Gross Acres: </span>
            {props.surfaceTract.acres}
            <span style={{ paddingLeft: '1rem', fontWeight: 600 }}>Partial: </span>
            {props.surfaceTract.isPartial ? '✅' : '❌'}
        </span>
    );

    const surfaceTractCell = (label: string, data: string | number, isBlank = false) => {
        if (isBlank) {
            return <span style={{ display: 'table-cell', width: '25%' }} />;
        }

        return (
            <span style={{ display: 'table-cell', width: '25%' }}>
                <span
                    style={{
                        width: '50%',
                        background: '#ccc',
                        color: '#555',
                        fontSize: '.9em',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        padding: '.2rem 0 .2rem 1rem',
                    }}
                >
                    {label}
                </span>
                <span
                    style={{
                        width: '50%',
                        textAlign: 'right',
                        color: '#6393c1',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        fontSize: '.75em',
                        padding: '.2rem 1rem .2rem 0',
                    }}
                >
                    {MathUtil.cleanNumber((data as number))}
                </span>
            </span>
        );
    };

    const acreagePanel = (ownerships: Array<Ownership>) => {
        let netAcres = 0;
        let decimalInterest = 0;

        ownerships.forEach((o: Ownership) => {
            netAcres += props.surfaceTract.acres * o.decimalInterest;
            decimalInterest += o.decimalInterest;
        });

        return (
            <div style={{ display: 'table', width: '100%', justifyContent: 'space-between', border: '1px solid #ccc' }}>
                {surfaceTractCell('Decimal Interest', decimalInterest)}
                {surfaceTractCell('Net', netAcres)}
            </div>
        );
    };

    const ownershipPanel = (ownerships: Array<Ownership>) => {
        const style: React.CSSProperties = {
            borderTop: '1px solid #ccc',
        };

        return (
            <div style={style} className="card-inner-hoverable-panel">
                {acreagePanel(ownerships)}
            </div>
        );
    };

    const renderOwners = (ownerships: Ownership[]) => {
        if (!ownerships) {
            return <div>No Ownerships on Surface Tract</div>;
        } else {
            return ownershipPanel(ownerships);
        }
    };

    return (
        <Card
            onClick={(e) => props.onClick(e)}
            badge={`${props.surfaceTract.number}`}
            rightBadgePanel={badgeInfo}
            contextActions={props.actions}
            newContext={true}
            showMenu={true}
            style={props?.surfaceTract?.isActive === true ? {backgroundColor: '#63c191'} : {}}
        >
            <div>
                {/* TODO: This Tags piece doesn't seem to be rendering properly. It just leaves a giant blank space in the Tract Card. */}
                {props?.surfaceTract?.tags?.length > 0 && (
                    <div style={{ width: '100%', padding: '1rem 1rem' }}>
                        <span 
                            style={{ visibility: (props.surfaceTract.tags?.length ? props.surfaceTract.tags?.length : 0) < 1 ? 'hidden' : 'visible',
                            fontWeight: 'bold', display: 'inline-block', marginRight: '.6rem', whiteSpace: 'nowrap' }}
                        >Tags:
                        </span>
                        <span>
                            {AgreementUtil.tagsToString(props.surfaceTract?.tags)}
                        </span>
                    </div>
                )}
                <div style={{ width: '100%', padding: '1rem 1rem' }}>
                    <span
                        style={{
                            maxHeight: '2rem',
                            width: '65%',
                            position: 'relative',
                            top: '.5rem',
                            display: 'inline-flex',
                        }}
                    >
                        <span style={{ fontWeight: 'bold', display: 'inline-block', marginRight: '.6rem', whiteSpace: 'nowrap' }}>Tract Name:</span>
                        <span>{props.surfaceTract.name}</span>
                    </span>
                </div>
                <div style={{ width: '100%', padding: '1rem 1rem' }}>
                    <span
                        style={{
                            maxHeight: '2rem',
                            width: '65%',
                            position: 'relative',
                            top: '.5rem',
                            display: 'inline-flex',
                        }}
                    >
                        <span
                            onMouseOver={() => toggleLegalDesc(true)}
                            onMouseOut={() => toggleLegalDesc(false)}
                            style={{ fontWeight: 'bold', display: 'inline-block', marginRight: '.6rem', whiteSpace: 'nowrap' }}
                        >
                            <span>👁️</span> Legal Description:
                        </span>
                        <span
                            ref={legDescEl}
                            style={{
                                display: 'inline-block',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                position: 'absolute',
                                border: '1px solid transparent',
                                padding: '.3rem',
                                top: '-0.5rem',
                                marginLeft: '15rem',
                                background: 'white',
                                zIndex: 0,
                                height: 'auto',
                            }}
                        >
                            {props.surfaceTract.legalDescription}
                        </span>
                    </span>
                </div>
                {renderOwners(props.surfaceTract.ownerships)}
            </div>
        </Card>
    );
};

export default SurfaceTractListCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/SurfaceTractListCard.tsx